import React from 'react';
import { Phone, Mail, X, CheckCircle, Edit2, Trash2, UserCheck, ChevronDown, RotateCcw, Briefcase, Clock, Calendar } from 'lucide-react';
import { formatPhoneNumber, formatCurrency, getInitials } from '../../utils/helpers';
import MapLink from '../MapLink';
import FollowUpChips from './FollowUpChips';

/**
 * Mobile drawer for lead details
 * Now with clear distinction between NEW and RETURNING customers
 */
const LeadDetailDrawer = ({
  lead,
  isOwner,
  onClose,
  onUpdateStatus,
  onEdit,
  onConvert,
  onDelete,
  saveStatus,
  onSetFollowUp,
  getFollowUpInfo,
  hasCompleteAddress,
  getLeadMissingFields,
  formatFullAddress,
  getStatusColor,
}) => {
  if (!lead) return null;

  // Determine if this is a returning customer
  const isReturningCustomer = !!lead.matchedCustomerId;
  const isLost = lead.status === 'Lost';

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto">
        {/* Handle */}
        <div className="sticky top-0 bg-white pt-3 pb-2 px-4 border-b border-gray-100 z-10">
          <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Avatar with returning indicator */}
              <div className="relative">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold ${
                  isReturningCustomer 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-primary-100 text-primary-700'
                }`}>
                  {getInitials(lead.name)}
                </div>
                {isReturningCustomer && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                    <RotateCcw size={12} className="text-white" />
                  </div>
                )}
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{lead.name}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusColor(lead.status)}`}>
                    {lead.status}
                  </span>
                  {isLost && lead.lostReason && (
                    <span className="text-xs text-gray-500">· {lead.lostReason}</span>
                  )}
                  {/* Returning customer badge */}
                  {isReturningCustomer && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                      <RotateCcw size={10} />
                      Returning
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* RETURNING CUSTOMER CALLOUT - Prominent! */}
        {isReturningCustomer && lead.status !== 'Converted' && !isLost && (
          <div className="mx-4 mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <RotateCcw size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-blue-900">Returning Customer!</p>
                <p className="text-sm text-blue-700 mt-1">
                  {lead.name} is already in your customer list. Click "Create Job" to add a new job for them - no duplicate customer will be created.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Contact Actions - Big touch targets */}
        <div className="p-4 space-y-3">
          {lead.phone && (
            <a 
              href={`tel:${lead.phone}`}
              className="flex items-center gap-4 p-4 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 transition-colors"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Phone size={20} />
              </div>
              <div>
                <p className="font-medium">Call</p>
                <p className="text-sm text-green-600">{formatPhoneNumber(lead.phone)}</p>
              </div>
            </a>
          )}

          {lead.email && (
            <a 
              href={`mailto:${lead.email}`}
              className="flex items-center gap-4 p-4 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Mail size={20} />
              </div>
              <div>
                <p className="font-medium">Email</p>
                <p className="text-sm text-blue-600 break-all">{lead.email}</p>
              </div>
            </a>
          )}

          {(lead.address || lead.city) && (
            <MapLink
              address={lead.address}
              city={lead.city}
              state={lead.state}
              zipCode={lead.zipCode}
              className="flex items-center gap-4 p-4 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors w-full"
            >
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-gray-600">📍</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">Directions</p>
                <p className="text-sm text-gray-500 truncate">{formatFullAddress(lead)}</p>
              </div>
            </MapLink>
          )}
        </div>

        {/* Lead Details */}
        <div className="px-4 pb-4 space-y-4">
          {/* Service & Amount - Owner sees amount, team member sees service only */}
          {(lead.serviceInterest || (isOwner && lead.estimatedAmount)) && (
            <div className="flex gap-3">
              {lead.serviceInterest && (
                <div className="flex-1 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Service Interest</p>
                  <p className="font-medium text-gray-900">{lead.serviceInterest}</p>
                </div>
              )}
              {/* OWNER ONLY: Estimated Value */}
              {isOwner && lead.estimatedAmount && (
                <div className="flex-1 bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">
                    {lead.hasInstantQuote ? '✨ Instant Quote' : 'Estimated Value'}
                  </p>
                  <p className="font-semibold text-green-600">{formatCurrency(lead.estimatedAmount)}</p>
                </div>
              )}
            </div>
          )}

          {/* OWNER ONLY: Custom Quote Needed Warning */}
          {isOwner && !lead.hasInstantQuote && !lead.estimatedAmount && lead.status !== 'Converted' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                📝 Custom Quote Needed
              </p>
              <p className="text-amber-600 text-xs mt-1">
                This service requires a custom quote. Tap "Edit" to add an estimated amount, or create the job and add pricing later.
              </p>
            </div>
          )}

          {/* Preferred Schedule */}
          {lead.preferredSchedule && (
            <div className="bg-purple-50 rounded-lg p-3 flex items-center gap-3">
              <Calendar size={18} className="text-purple-600" />
              <div>
                <p className="text-xs text-gray-500">Preferred Timing</p>
                <p className="font-medium text-purple-700">{lead.preferredSchedule}</p>
              </div>
            </div>
          )}

          {/* Message */}
          {lead.message && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Customer Message</p>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">
                {lead.message}
              </p>
            </div>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Source</p>
              <p className="font-medium text-gray-900">{lead.source || 'Unknown'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Submitted</p>
              <p className="font-medium text-gray-900">{new Date(lead.createdAt).toLocaleDateString()}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">Verified</p>
              <p className={`font-medium ${lead.isVerified ? 'text-green-600' : 'text-gray-900'}`}>
                {lead.isVerified ? '✓ Yes' : 'No'}
              </p>
            </div>
            {/* OWNER ONLY: Discount Eligible */}
            {isOwner ? (
              <div className={`rounded-lg p-3 ${lead.isDiscountEligible ? 'bg-orange-50' : 'bg-gray-50'}`}>
                <p className="text-xs text-gray-500">Discount Eligible</p>
                <p className={`font-medium ${lead.isDiscountEligible ? 'text-orange-600' : 'text-gray-900'}`}>
                  {lead.isDiscountEligible ? '🎖️ Yes' : 'No'}
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Customer Type</p>
                <p className="font-medium text-gray-900">
                  {isReturningCustomer ? 'Returning' : 'New'}
                </p>
              </div>
            )}
          </div>

          {/* Follow-up reminder */}
          <FollowUpChips
            lead={lead}
            onSetFollowUp={onSetFollowUp}
            getFollowUpInfo={getFollowUpInfo}
          />

          {/* Status Selector - Not for Converted leads OR returning customers */}
          {lead.status !== 'Converted' ? (
            isReturningCustomer ? (
              /* Returning customer - no status tracking needed */
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-blue-700 text-sm">
                  {isLost ? 'Marked as Lost. Tap Reopen below if they come back.' : 'Ready to create a job for this returning customer'}
                </p>
              </div>
            ) : (
              /* New customer - show status dropdown */
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">Update Status</p>
                  {saveStatus === 'saving' && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <span className="animate-spin">⏳</span> Saving...
                    </span>
                  )}
                  {saveStatus === 'saved' && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle size={12} /> Saved
                    </span>
                  )}
                </div>
                <div className="relative">
                  <select
                    value={lead.status}
                    onChange={(e) => onUpdateStatus(lead.id, e.target.value)}
                    className="w-full appearance-none bg-white border border-gray-200 rounded-lg pl-4 pr-10 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="New">New</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Quoted">Quoted</option>
                    <option value="Lost">Lost</option>
                  </select>
                  <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <CheckCircle size={24} className="inline text-green-600 mb-2" />
              <p className="text-green-800 font-medium">Converted to Customer</p>
              <p className="text-sm text-green-600 mt-1">
                {lead.convertedAt && new Date(lead.convertedAt).toLocaleDateString()}
              </p>
              <p className="text-xs text-green-600 mt-2">
                This lead is now read-only
              </p>
              {isOwner && (
                <button
                  onClick={() => onDelete(lead)}
                  className="mt-4 w-full py-3 border-2 border-red-200 text-red-600 font-medium rounded-xl bg-white hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  Delete Lead
                </button>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons - Only show for non-Converted leads */}
        {lead.status !== 'Converted' && (
          <div className="p-4 border-t border-gray-100 space-y-2">
            {/* Incomplete Address Warning */}
            {!isLost && !hasCompleteAddress(lead) && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-2">
                <p className="text-orange-800 text-sm font-medium flex items-center gap-2">
                  ⚠️ Missing Information
                </p>
                <p className="text-orange-600 text-xs mt-1">
                  {getLeadMissingFields(lead).join(', ')} required before creating job.
                </p>
              </div>
            )}

            {/* Edit Button */}
            <button
              onClick={() => onEdit(lead)}
              className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <Edit2 size={18} />
              Edit Lead
            </button>

            {/* Lost leads: Reopen replaces Convert */}
            {isLost && (
              <button
                onClick={() => onUpdateStatus(lead.id, 'New')}
                className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                <RotateCcw size={18} />
                Reopen Lead
              </button>
            )}

            {/* Convert / Create Job and its helper text (not shown for Lost leads) */}
            {!isLost && (
            <>
            <button
              onClick={() => onConvert(lead)}
              disabled={!hasCompleteAddress(lead)}
              className={`w-full py-3 font-medium rounded-xl transition-colors flex items-center justify-center gap-2 ${
                hasCompleteAddress(lead)
                  ? isReturningCustomer
                    ? 'bg-blue-600 text-white hover:bg-blue-700'   // Returning - blue
                    : 'bg-green-600 text-white hover:bg-green-700' // New - green
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isReturningCustomer ? (
                <>
                  <Briefcase size={18} />
                  Create Job for {lead.name.split(' ')[0]}
                </>
              ) : (
                <>
                  <UserCheck size={18} />
                  Convert to Customer
                </>
              )}
            </button>
            
            {/* Helper text for the button */}
            {hasCompleteAddress(lead) && (
              <p className="text-xs text-center text-gray-500">
                {isReturningCustomer 
                  ? "This will create a new job linked to their existing customer profile"
                  : "This will create a new customer and a job"
                }
              </p>
            )}

            </>
            )}

            {/* Returning customers have no status dropdown, so give them a way to mark Lost */}
            {isReturningCustomer && !isLost && (
              <button
                onClick={() => onUpdateStatus(lead.id, 'Lost')}
                className="w-full py-3 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors"
              >
                Mark as Lost
              </button>
            )}

            {/* OWNER ONLY: Delete button */}
            {isOwner && (
              <button
                onClick={() => onDelete(lead)}
                className="w-full py-3 border-2 border-red-200 text-red-600 font-medium rounded-xl hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                Delete Lead
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadDetailDrawer;